import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cloud } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { Button } from '@/components/ui/button';
import { esCount } from '@/lib/es-count';
import { notifyErr, notifyOk } from '@/lib/notify';
import { useSyncStripeCatalog } from '../orders/hooks/useStoreOrders';
import { StripeModeNotice } from './StripeModeNotice';
import { useStripeMode } from './useStripeMode';

export function StorePaymentsPage() {
    const mode = useStripeMode();
    const syncStripe = useSyncStripeCatalog();
    const [confirmLive, setConfirmLive] = useState(false);
    const stripeMode = mode.data;
    const blocked = stripeMode === 'missing' || mode.isLoading || mode.isError;

    const runSync = () => {
        syncStripe.mutate(
            {},
            {
                onSuccess: (data) => {
                    const synced = data?.synced;
                    const failed = data?.failed;
                    const syncedText = synced == null
                        ? 'Catálogo sincronizado con Stripe'
                        : `Sincronizadas ${esCount(synced, 'variante')}`;
                    const failedText = failed
                        ? `. ${esCount(failed, 'fallida')}`
                        : '';
                    notifyOk(`${syncedText}${failedText}`);
                },
                onError: (err) => {
                    notifyErr(err instanceof Error ? err.message : 'Error sincronizando con Stripe');
                },
            },
        );
    };

    return (
        <div className="space-y-6">
            <StripeModeNotice mode={mode.isError ? 'unknown' : stripeMode} pending={mode.isLoading} />
            {mode.isError ? (
                <p className="text-sm text-destructive">
                    {(mode.error as Error).message || 'No se ha podido leer el modo de Stripe.'}
                </p>
            ) : null}

            <section className="space-y-3 rounded-xl border border-border bg-card p-4">
                <h2 className="text-sm font-semibold">Catálogo de merch en Stripe</h2>
                <p className="text-sm text-muted-foreground">
                    Crea o actualiza los precios de las variantes activas del merch. No cambia pedidos ya cobrados.
                </p>
                <Button
                    type="button"
                    variant="outline"
                    disabled={blocked || syncStripe.isPending}
                    onClick={() => {
                        if (stripeMode === 'live' || stripeMode === 'unknown') {
                            setConfirmLive(true);
                            return;
                        }
                        runSync();
                    }}
                >
                    <Cloud size={14} />
                    {syncStripe.isPending ? 'Sincronizando…' : 'Sync Stripe'}
                </Button>
            </section>

            <section className="space-y-2 rounded-xl border border-border bg-card p-4">
                <h2 className="text-sm font-semibold">Facturas</h2>
                <p className="text-sm text-muted-foreground">
                    Las facturas de tienda y sus series (AIP-T y el histórico AIP) se consultan en Operaciones.
                    Aquí no hay otra serie que configurar.
                </p>
                <Link to="/operations/emitidas" className="text-sm font-medium text-primary hover:underline">
                    Operaciones → Facturas emitidas
                </Link>
            </section>

            <AdminConfirmDialog
                open={confirmLive}
                title="Sincronizar con Stripe"
                description="La clave de este entorno no es de pruebas. Sync Stripe creará o actualizará precios en esa cuenta."
                confirmLabel="Sincronizar"
                variant="danger"
                onCancel={() => setConfirmLive(false)}
                onConfirm={() => {
                    setConfirmLive(false);
                    runSync();
                }}
            />
        </div>
    );
}
